import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { IMG, INSIGHT_CATEGORIES } from "@/lib/content";
import { endpoints, mediaUrl } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Backdrop } from "@/components/IntersectMotif";

export default function Insights() {
  const { t, isAr } = useLang();
  const [cat, setCat] = useState("All");
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["insights", cat],
    queryFn: () => endpoints.insights(cat === "All" ? null : cat),
  });

  const { data: all = [] } = useQuery({ queryKey: ["insights", "All"], queryFn: () => endpoints.insights(null) });
  const allCategories = ["All", ...Array.from(new Set([...INSIGHT_CATEGORIES.slice(1), ...all.map((a) => a.category)].filter(Boolean)))];

  const [featured, ...rest] = items;

  return (
    <>
      <PageHero eyebrow="Perspective" title="Insights on capital, partnerships and Saudi opportunity." image={IMG.geometric}
        subtitle="Perspectives from Intersect on investment, markets and the Saudi economy." />

      <section className="section-pad relative overflow-hidden">
        <Backdrop src={IMG.faisaliah} opacity={0.1} />
        <div className="container-x relative z-10">
          <div className="flex flex-wrap gap-3 mb-14" data-testid="insight-filters">
            {allCategories.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`cat-${c.replace(/\s/g, "-").toLowerCase()}`}
                className={`px-5 py-2.5 text-xs tracking-wide uppercase border transition-colors duration-300 ${cat === c ? "border-primary-gold text-primary-gold" : "border-white/10 text-muted-text hover:border-primary-gold/50 hover:text-soft-stone"}`}>
                {t(c)}
              </button>
            ))}
          </div>

          {isLoading ? <p className="text-muted-text">{t("Loading…")}</p> : items.length === 0 ? (
            <div className="border border-primary-gold/15 p-16 text-center"><h3 className="text-2xl font-light text-ivory">{t("No perspectives in this category yet.")}</h3></div>
          ) : (
            <>
              {featured && (
                <Reveal>
                  <Link to={`/insights/${featured.slug}`} className="group grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16 items-center" data-testid="featured-insight">
                    <div className="relative overflow-hidden aspect-[16/10]">
                      <img src={mediaUrl(featured.hero_image)} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div>
                      <div className="flex items-center gap-3 text-xs tracking-wide uppercase text-primary-gold mb-4"><span>{t(featured.category)}</span><span className="text-white/20">·</span><span className="text-muted-text">{featured.reading_time}</span></div>
                      <h2 className="text-3xl md:text-4xl font-light tracking-tight text-ivory group-hover:text-primary-gold transition-colors">{isAr && featured.title_ar ? featured.title_ar : featured.title}</h2>
                      <p className="mt-6 text-lg leading-relaxed text-muted-text">{isAr && featured.excerpt_ar ? featured.excerpt_ar : featured.excerpt}</p>
                      <p className="mt-6 text-xs text-muted-text">{featured.date}</p>
                    </div>
                  </Link>
                </Reveal>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                {rest.map((a, i) => (
                  <Reveal key={a.id} delay={0.06 * i}>
                    <Link to={`/insights/${a.slug}`} className="group block card-lift" data-testid={`insight-${i}`}>
                      <div className="relative overflow-hidden aspect-[4/3] mb-6"><img src={mediaUrl(a.hero_image)} alt={a.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                      <div className="flex items-center gap-3 text-xs tracking-wide uppercase text-primary-gold mb-3"><span>{t(a.category)}</span><span className="text-white/20">·</span><span className="text-muted-text">{a.reading_time}</span></div>
                      <h3 className="text-xl font-light text-ivory group-hover:text-primary-gold transition-colors">{isAr && a.title_ar ? a.title_ar : a.title}</h3>
                      <p className="mt-2 text-xs text-muted-text">{a.date}</p>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
